import React, { useEffect, useState } from 'react';
import { AccountNumber, CardWrapper } from 'modules/PaymentProfile/styles';
import EditModal from 'modules/PaymentProfile/components/EditModal';
import { Button, Icons, Label } from '@myob/myob-widgets';
import masterCardIcon from 'assets/images/masterCard.svg';
import americaExpressIcon from 'assets/images/americanExpress.svg';
import visaIcon from 'assets/images/visa.svg';
import {
    ECreditCardType,
    EPaymentProfileActionType,
    IPaymentProfileItem,
    IPostPaymentProfileAction,
} from 'modules/PaymentProfile/reducers/type';
import { ValidatedPaymentCard } from 'modules/PaymentProfile/type';
import { useDispatch, useSelector } from 'react-redux';
import { getBusinessId } from 'helpers/query';
import { RootState } from 'stores';
import { ENotificationType, NotificationState } from 'stores/reducers/notification/type';
import * as telemetry from 'telemetry';
import { isCreditCardPaymentProfile, isDirectDebitPaymentProfile } from 'helpers/paymentProfile';
import { ETelemetryActionsAndLabels, ETelemetryNames } from 'telemetry/type';
import RegionWrapper from 'components/RegionWrapper';
import { generateAUDirectDebitDetail, generateNZDirectDebitDetail } from 'helpers/tools';

type BankCardProps = {
    paymentItem: IPaymentProfileItem;
};

const iconMap = {
    [ECreditCardType.MasterCard]: masterCardIcon,
    [ECreditCardType.Visa]: visaIcon,
    [ECreditCardType.AmericanExpress]: americaExpressIcon,
};

const BankCard: React.FC<BankCardProps> = ({ paymentItem }) => {
    const [showModal, setShowModal] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);

    const dispatch = useDispatch();
    const notification = useSelector<RootState, NotificationState>(state => state.notification);

    useEffect(() => {
        const { type, secondaryMessage } = notification;
        if (type === ENotificationType.Clear) {
            return;
        }
        setShowModal(false);
        setShowSpinner(false);
        const optionalErrorInformation =
            type === ENotificationType.Success
                ? {}
                : {
                      actualError: secondaryMessage,
                  };
        telemetry.track(ETelemetryNames.PaymentProfile, {
            action: ETelemetryActionsAndLabels.Create,
            label: ETelemetryActionsAndLabels.Create,
            createData: {
                status: type === ENotificationType.Success ? 'success' : 'fail',
                ...optionalErrorInformation,
            },
        });
    }, [notification]);

    const businessId = getBusinessId();

    const onCardEdit = ({ detail }: ValidatedPaymentCard) => {
        const action: IPostPaymentProfileAction = {
            type: EPaymentProfileActionType.POST_REQUESTED,
            payload: {
                data: detail,
                params: { businessId },
            },
            meta: {
                successMessage: 'Success! Your payment details have been added.',
                failureMessage:
                    "Sorry, we couldn't update your payment details. Please try again in a few minutes or contact our support team.",
            },
        };
        dispatch(action);
        setShowSpinner(true);
    };
    return (
        <CardWrapper>
            {showModal && (
                <EditModal
                    isProcessing={showSpinner}
                    cancelModal={(): void => setShowModal(false)}
                    onSaveValidData={onCardEdit}
                />
            )}
            <div id="status">
                <Label type="boxed" color="green">
                    {paymentItem.status}
                </Label>
                <Button type="link" icon={<Icons.Edit />} iconLeft onClick={(): void => setShowModal(true)}>
                    Edit
                </Button>
            </div>
            {isCreditCardPaymentProfile(paymentItem) ? (
                <AccountNumber>
                    <span data-test={'e2e-last-four-digits'}>•••• {paymentItem.credit_card.last_four_digits}</span>
                    <img src={iconMap[paymentItem.credit_card.type]} alt="credit card icon" />
                </AccountNumber>
            ) : (
                <AccountNumber>
                    <div>
                        <p data-test="direct-debit-payment-frequency">
                            Charged {paymentItem.payment_schedule.toLowerCase()}
                        </p>
                        <span data-test={'e2e-last-four-digits'}>
                            {isDirectDebitPaymentProfile(paymentItem) && (
                                <RegionWrapper
                                    NZChild={generateNZDirectDebitDetail(paymentItem)}
                                    AUChild={generateAUDirectDebitDetail(paymentItem)}
                                />
                            )}
                        </span>
                    </div>
                    <Icons.Bank set="lg" />
                </AccountNumber>
            )}
        </CardWrapper>
    );
};

export default BankCard;
